import { Linking, Platform } from 'react-native';
import { WebViewMessage } from 'react-native-webview/lib/WebViewTypes';

import { Share } from '@/feature/entry-share/utils/share';
import { GlobalConfigKeys, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { logger } from '@/utils/logging';

const MessageType = {
    CALL_NATIVE_SHARE: 'CALL_NATIVE_SHARE',
    CALL_NATIVE_NAVIGATION: 'CALL_NATIVE_NAVIGATION',
} as const;

const FIRST_PARTY_HOST = 'betr.app';

// Prismic can only add hosts, so a bad edit there can't lock out first-party pages
const getTrustedHosts = (): string[] => {
    const prismicHosts = Object.keys(
        useJurisdictionStore.getState().jurisdictionSettings?.globalSettings?.keyValuePairs?.[
            GlobalConfigKeys.TrustedWebviewHosts
        ] ?? {}
    )
        .map(host => host.trim().toLowerCase())
        .filter(Boolean);
    return [FIRST_PARTY_HOST, ...prismicHosts];
};

export const isTrustedWebviewUrl = (url: string): boolean => {
    try {
        const { protocol, hostname } = new URL(url);
        // the RN URL polyfill does not lowercase the host like browsers and node do
        const host = hostname.toLowerCase();
        return (
            protocol === 'https:' && getTrustedHosts().some(trusted => host === trusted || host.endsWith(`.${trusted}`))
        );
    } catch {
        return false;
    }
};

export const handleWebviewMessage = async (nativeEvent: WebViewMessage, linkTo?: (to: string) => void) => {
    if (!isTrustedWebviewUrl(nativeEvent?.url ?? '')) {
        logger.warn('ModalWebView-onMessage', 'ignored message from untrusted origin', nativeEvent?.url);
        return;
    }
    try {
        const action = JSON.parse(nativeEvent?.data || '{}');
        switch (action?.type) {
            case MessageType.CALL_NATIVE_SHARE:
                // RN's share feature is used for reasons that browser share feature is not supported in Android webview.
                // Message source: https://github.com/betrapp/promos/pull/13/files#diff-c72d0ddf542005278f505746af2a70a9209256243880125580e8e85b2e52adc4R62-R64
                await Share.open({ ...action.payload, failOnCancel: false });
                break;
            case MessageType.CALL_NATIVE_NAVIGATION: {
                const target = action.payload?.linkTo;
                const promoSlug = action.payload?.promoSlug;
                if (typeof target !== 'string') {
                    break;
                }
                if (target === 'deposit' && typeof promoSlug === 'string' && promoSlug) {
                    // only send the promo slug if we're redirecting to the deposit page
                    linkTo?.(`/${target}?promoSlug=${encodeURIComponent(promoSlug)}`);
                    break;
                }
                linkTo?.('/' + target);
                break;
            }
            default:
                break;
        }
    } catch (error) {
        logger.error('ModalWebView-onMessage', error);
    }
};

export const openExternalUrlAndroid = async (eventUrl: string) => {
    /* Checking whether there was a failed attempt to open an external URL (Android) */
    if (eventUrl?.includes('#Intent') && Platform.OS === 'android') {
        /* Removing "#Intent" string added by android to initial url*/
        const externalUrl = eventUrl.split('#Intent;')[0];
        /* Checking whether intent external url is supported */
        const urlIsSupported = await Linking.canOpenURL(externalUrl);
        if (urlIsSupported) {
            /* Opening external url */
            await Linking.openURL(externalUrl);
        } else {
            /* Opening external url on external browser */
            await Linking.openURL(externalUrl.replace('intent', 'https'));
        }
    }
};
